import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { LocationCity, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { projectsApi } from '../services/api';
import { CreateProjectRequest } from '../types/project';

const steps = ['Project Details', 'Review & Create'];

export default function CreateProject() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateProjectRequest>({
    name: '',
    description: '',
    ordinance_type: undefined,
    municipality: '',
    state: '',
  });


  const handleInputChange = (field: keyof CreateProjectRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    setError(null);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!formData.name.trim()) {
        setError('Please enter a project name');
        return;
      }
      if (!formData.municipality.trim()) {
        setError('Please enter a municipality');
        return;
      }
      if (!formData.state.trim()) {
        setError('Please enter a state');
        return;
      }
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate('/');
      return;
    }
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const project = await projectsApi.createProject(formData);
      navigate(`/projects/${project.id}`);
    } catch (err) {
      console.error('Error creating project:', err);
      setError('Failed to create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Project Information
            </Typography>

            <TextField
              label="Project Name"
              variant="outlined"
              fullWidth
              required
              value={formData.name}
              onChange={handleInputChange('name')}
              sx={{ mb: 3 }}
              placeholder="e.g., Downtown Subdivision Phase II"
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Municipality"
                variant="outlined"
                fullWidth
                required
                value={formData.municipality}
                onChange={handleInputChange('municipality')}
                placeholder="e.g., Springfield"
              />
              <TextField
                label="State"
                variant="outlined"
                fullWidth
                required
                select
                value={formData.state}
                onChange={handleInputChange('state')}
              >
                <MenuItem value="">
                  <em>Select State</em>
                </MenuItem>
                <MenuItem value="PA">Pennsylvania (PA)</MenuItem>
                <MenuItem value="VA">Virginia (VA)</MenuItem>
                <MenuItem value="OH">Ohio (OH)</MenuItem>
              </TextField>
            </Box>

            <TextField
              label="Project Description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={formData.description}
              onChange={handleInputChange('description')}
              sx={{ mb: 3 }}
              placeholder="Describe the scope and requirements of your surveying project..."
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Review Project Details
            </Typography>

            <Card elevation={0} sx={{ border: 1, borderColor: 'grey.300', mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  {formData.name}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body1">
                    {formData.municipality}, {formData.state}
                  </Typography>
                </Box>

                {formData.ordinance_type && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Ordinance Type
                    </Typography>
                    <Typography variant="body1">
                      {formData.ordinance_type}
                    </Typography>
                  </Box>
                )}

                {formData.description && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {formData.description}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Alert severity="info" sx={{ mb: 3 }}>
              After creating your project, you'll be able to upload ordinance documents and track compliance requirements.
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', boxShadow: 1 }}>
        <Container maxWidth="lg">
          <Box sx={{ py: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationCity sx={{ color: 'primary.main', fontSize: 28 }} />
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  bgcolor: 'secondary.main',
                  borderRadius: '50%',
                }}
              />
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  bgcolor: 'primary.main',
                  borderRadius: '2px',
                  transform: 'rotate(45deg)',
                }}
              />
            </Box>
            <Typography variant="h6" sx={{ ml: 2, fontWeight: 700, color: 'primary.main' }}>
              CivilSteve
            </Typography>
            <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary', fontWeight: 500 }}>
              Create New Project
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step Content */}
          {renderStepContent()}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={handleBack}
              startIcon={<ArrowBack />}
              sx={{ fontWeight: 500 }}
            >
              {activeStep === 0 ? 'Back to Home' : 'Back'}
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  size="large"
                  sx={{ px: 4, fontWeight: 600 }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Creating Project...
                    </>
                  ) : (
                    'Create Project'
                  )}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  size="large"
                  sx={{ px: 4, fontWeight: 600 }}
                >
                  Continue
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}